// 売上ページ用の集計ヘルパー
// Transactions（取引明細）から各種視点で集計するロジックをまとめる。
import { airRegiAccountHour, airRegiBusinessDate, airRegiService } from './airRegiBusinessTime';

export interface TxnItem {
  category: string | null;
  menuName: string;
  unitPrice: number | null;
  quantity: number;
}

export interface TxnLite {
  storeId: string;
  txnNo: string;
  visitDate: string | null;
  visitTime: string | null;
  accountDate: string; // YYYY-MM-DD
  accountTime: string | null; // HH:mm:ss
  total: number;
  customerCount: number | null;
  itemCount: number | null;
  stayDurationSec: number | null;
  receiptName: string | null;
  resourceName: string | null;
  staffName: string | null;
  // 取込元: "airregi-kaikei" or "airregi-journal"。ジャーナル由来は人数・滞在時間・席名・カテゴリが欠損
  source?: string | null;
  byPayment?: { method: string; amount: number }[];
  items: TxnItem[];
}

// ジャーナル履歴由来かどうか判定（人数や滞在時間などの欠損項目があるかの目安）
export const isJournalTxn = (t: TxnLite): boolean => t.source === 'airregi-journal';

// 全取引のうち、ジャーナル履歴由来の取引数比率
export const journalRatio = (txns: TxnLite[]): number => {
  if (txns.length === 0) return 0;
  return txns.filter(isJournalTxn).length / txns.length;
};

export interface StoreLD {
  id: string;
  lunchStart: string | null;
  lunchEnd: string | null;
  dinnerStart: string | null;
  dinnerEnd: string | null;
  lunchAggregationEnd?: string | null;
  dinnerAggregationEnd?: string | null;
  airregiBusinessDayStart?: string | null;
  drinkCategoryKeywords?: string[] | null;
}

export type LDSeg = 'lunch' | 'dinner' | 'other';

const DEFAULT_DRINK_KEYWORDS = [
  'ドリンク',
  '飲料',
  'ソフトドリンク',
  'アルコール',
  '酒',
  'ビール',
  'ハイボール',
  'カクテル',
  'ワイン',
  '日本酒',
  '焼酎',
  'サワー',
];

export const classifyLD = (
  txn: TxnLite,
  stores: StoreLD[],
): LDSeg => {
  const store = stores.find((s) => s.id === txn.storeId);
  return airRegiService(txn, store);
};

const storeFor = (txn: TxnLite, stores: StoreLD[]): StoreLD | undefined =>
  stores.find((store) => store.id === txn.storeId);

export const salesBusinessDate = (txn: TxnLite, stores: StoreLD[] = []): string =>
  airRegiBusinessDate(txn, storeFor(txn, stores)?.airregiBusinessDayStart);

export const salesAccountHour = (txn: TxnLite, stores: StoreLD[] = []): number | null =>
  airRegiAccountHour(txn, storeFor(txn, stores)?.airregiBusinessDayStart);

export const filterByBusinessMonth = (
  txns: TxnLite[], stores: StoreLD[], yearMonth: string,
): TxnLite[] => txns.filter((txn) => salesBusinessDate(txn, stores).startsWith(`${yearMonth}-`));

// ドリンク判定。category が null の取引（ジャーナル履歴由来など）は menuName をフォールバックとして判定する。
export const isDrinkCategory = (
  category: string | null | undefined,
  keywordsByStore?: Record<string, string[]>,
  storeId?: string,
  menuName?: string | null,
): boolean => {
  const keywords = (storeId && keywordsByStore?.[storeId]?.length)
    ? keywordsByStore[storeId]
    : DEFAULT_DRINK_KEYWORDS;
  if (category && keywords.some((k) => category.includes(k))) return true;
  // category が空 or マッチしない → menuName 末尾語に対するフォールバック判定
  if (menuName) {
    return keywords.some((k) => menuName.includes(k));
  }
  return false;
};

// 取引時刻のフィルタ：L/D セグメント
export const filterByLD = (
  txns: TxnLite[],
  stores: StoreLD[],
  ld: 'all' | 'lunch' | 'dinner',
): TxnLite[] => {
  if (ld === 'all') return txns;
  return txns.filter((t) => classifyLD(t, stores) === ld);
};

// 客単価の分布（500 円刻み、10000 円超は最後の bucket にまとめる）
export interface SpendBin {
  range: string;
  rangeMin: number;
  count: number;
}
export const spendDistribution = (
  txns: TxnLite[],
  binSize = 500,
  maxBin = 10000,
): SpendBin[] => {
  const buckets = new Map<number, number>();
  const overflowKey = maxBin;
  for (const t of txns) {
    const cust = t.customerCount ?? 0;
    if (cust <= 0) continue;
    const spend = t.total / cust;
    const bin = spend >= maxBin ? overflowKey : Math.floor(spend / binSize) * binSize;
    buckets.set(bin, (buckets.get(bin) ?? 0) + 1);
  }
  const sortedKeys = Array.from(buckets.keys()).sort((a, b) => a - b);
  return sortedKeys.map((k) => ({
    range:
      k >= overflowKey
        ? `${(maxBin / 1000).toFixed(0)}k+`
        : `¥${k.toLocaleString()}`,
    rangeMin: k,
    count: buckets.get(k) ?? 0,
  }));
};

// 時間帯別客単価（hour: 0..23）
export interface HourSpend {
  hour: number;
  customerCount: number;
  txnCount: number;
  sales: number;
  avgSpend: number | null;
}
export const hourlySpend = (txns: TxnLite[], stores: StoreLD[] = []): HourSpend[] => {
  const map = new Map<number, { sales: number; cust: number; cnt: number }>();
  for (const t of txns) {
    const h = salesAccountHour(t, stores);
    if (h === null) continue;
    const cur = map.get(h) ?? { sales: 0, cust: 0, cnt: 0 };
    cur.sales += t.total;
    cur.cust += t.customerCount ?? 0;
    cur.cnt += 1;
    map.set(h, cur);
  }
  const out: HourSpend[] = [];
  const minH = Math.min(...Array.from(map.keys()));
  const maxH = Math.max(...Array.from(map.keys()));
  for (let h = minH; h <= maxH; h++) {
    const v = map.get(h);
    out.push({
      hour: h,
      customerCount: v?.cust ?? 0,
      txnCount: v?.cnt ?? 0,
      sales: v?.sales ?? 0,
      avgSpend: v && v.cust > 0 ? v.sales / v.cust : null,
    });
  }
  return out;
};

// パーティサイズ分布と客単価
export interface PartyBin {
  bucket: '1人' | '2人' | '3〜4人' | '5人以上';
  txnCount: number;
  customerCount: number;
  sales: number;
  avgSpend: number | null; // 売上 / 客数
  avgPartySpend: number | null; // 売上 / 取引数（組単価）
}
const partyBucketOf = (n: number): PartyBin['bucket'] | null => {
  if (n <= 0) return null;
  if (n === 1) return '1人';
  if (n === 2) return '2人';
  if (n <= 4) return '3〜4人';
  return '5人以上';
};
export const partySizeBins = (txns: TxnLite[]): PartyBin[] => {
  const order: PartyBin['bucket'][] = ['1人', '2人', '3〜4人', '5人以上'];
  const map = new Map<PartyBin['bucket'], { sales: number; cust: number; cnt: number }>();
  for (const t of txns) {
    const c = t.customerCount ?? 0;
    const b = partyBucketOf(c);
    if (!b) continue;
    const cur = map.get(b) ?? { sales: 0, cust: 0, cnt: 0 };
    cur.sales += t.total;
    cur.cust += c;
    cur.cnt += 1;
    map.set(b, cur);
  }
  return order.map((b) => {
    const v = map.get(b);
    return {
      bucket: b,
      txnCount: v?.cnt ?? 0,
      customerCount: v?.cust ?? 0,
      sales: v?.sales ?? 0,
      avgSpend: v && v.cust > 0 ? v.sales / v.cust : null,
      avgPartySpend: v && v.cnt > 0 ? v.sales / v.cnt : null,
    };
  });
};

// 曜日 × 時間帯ヒートマップ
//   wday: 0=日, 1=月, ..., 6=土
export interface WdayHourCell {
  wday: number;
  hour: number;
  txnCount: number;
  sales: number;
}
export const wdayHourMatrix = (
  txns: TxnLite[], stores: StoreLD[] = [],
): { cells: WdayHourCell[]; minHour: number; maxHour: number } => {
  let minH = Infinity;
  let maxH = 0;
  const map = new Map<string, { cnt: number; sales: number }>();
  for (const t of txns) {
    const h = salesAccountHour(t, stores);
    if (h === null) continue;
    if (h < minH) minH = h;
    if (h > maxH) maxH = h;
    const wday = new Date(`${salesBusinessDate(t, stores)}T00:00:00Z`).getUTCDay();
    const key = `${wday}-${h}`;
    const cur = map.get(key) ?? { cnt: 0, sales: 0 };
    cur.cnt += 1;
    cur.sales += t.total;
    map.set(key, cur);
  }
  if (minH > maxH) return { cells: [], minHour: 0, maxHour: 0 };
  const cells: WdayHourCell[] = [];
  for (let w = 0; w < 7; w++) {
    for (let h = minH; h <= maxH; h++) {
      const v = map.get(`${w}-${h}`);
      cells.push({ wday: w, hour: h, txnCount: v?.cnt ?? 0, sales: v?.sales ?? 0 });
    }
  }
  return { cells, minHour: minH, maxHour: maxH };
};

// 日別推移
export interface DayPoint {
  date: string;
  sales: number;
  customerCount: number;
  txnCount: number;
}
export const dailySeries = (txns: TxnLite[], stores: StoreLD[] = []): DayPoint[] => {
  const map = new Map<string, DayPoint>();
  for (const t of txns) {
    const date = salesBusinessDate(t, stores);
    const cur = map.get(date) ?? {
      date,
      sales: 0,
      customerCount: 0,
      txnCount: 0,
    };
    cur.sales += t.total;
    cur.customerCount += t.customerCount ?? 0;
    cur.txnCount += 1;
    map.set(date, cur);
  }
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
};

// 滞在時間分布（分単位の bin）
export interface StayBin {
  label: string;
  rangeMin: number; // 分
  count: number;
}
export const stayDurationDist = (txns: TxnLite[]): StayBin[] => {
  // bin 区切り (min): 0-30, 30-60, 60-90, 90-120, 120+
  const bins = [
    { label: '〜30分', min: 0, max: 30 },
    { label: '30〜60分', min: 30, max: 60 },
    { label: '60〜90分', min: 60, max: 90 },
    { label: '90〜120分', min: 90, max: 120 },
    { label: '120分超', min: 120, max: Infinity },
  ];
  const out: StayBin[] = bins.map((b) => ({ label: b.label, rangeMin: b.min, count: 0 }));
  for (const t of txns) {
    if (t.stayDurationSec == null || t.stayDurationSec <= 0) continue;
    const min = t.stayDurationSec / 60;
    const idx = bins.findIndex((b) => min >= b.min && min < b.max);
    if (idx >= 0) out[idx].count += 1;
  }
  return out;
};

// 時間帯別 F/D 比率
export interface HourFD {
  hour: number;
  food: number;
  drink: number;
  drinkRatio: number | null;
}
export const hourlyFoodDrink = (
  txns: TxnLite[],
  drinkKeywordsByStore?: Record<string, string[]>,
  stores: StoreLD[] = [],
): HourFD[] => {
  const map = new Map<number, { food: number; drink: number }>();
  for (const t of txns) {
    const h = salesAccountHour(t, stores);
    if (h === null) continue;
    const cur = map.get(h) ?? { food: 0, drink: 0 };
    for (const it of t.items ?? []) {
      const sub = (it.unitPrice ?? 0) * (it.quantity ?? 0);
      if (isDrinkCategory(it.category, drinkKeywordsByStore, t.storeId, it.menuName)) cur.drink += sub;
      else cur.food += sub;
    }
    map.set(h, cur);
  }
  const sortedH = Array.from(map.keys()).sort((a, b) => a - b);
  return sortedH.map((h) => {
    const v = map.get(h)!;
    const total = v.food + v.drink;
    return {
      hour: h,
      food: v.food,
      drink: v.drink,
      drinkRatio: total > 0 ? (v.drink / total) * 100 : null,
    };
  });
};

// カテゴリ × 時間帯ヒートマップ（売上）
export interface CategoryHourCell {
  category: string;
  hour: number;
  sales: number;
}
export interface CategoryHourGrid {
  cells: CategoryHourCell[];
  categories: string[];
  hours: number[];
}
export const categoryHourMatrix = (
  txns: TxnLite[],
  topN = 8,
  stores: StoreLD[] = [],
): CategoryHourGrid => {
  const catTotals = new Map<string, number>();
  let minH = Infinity;
  let maxH = 0;
  const cellMap = new Map<string, number>(); // key=`${cat}|${h}`
  for (const t of txns) {
    const h = salesAccountHour(t, stores);
    if (h === null) continue;
    if (h < minH) minH = h;
    if (h > maxH) maxH = h;
    for (const it of t.items ?? []) {
      const cat = (it.category ?? '未分類').toString();
      const sub = (it.unitPrice ?? 0) * (it.quantity ?? 0);
      catTotals.set(cat, (catTotals.get(cat) ?? 0) + sub);
      const key = `${cat}|${h}`;
      cellMap.set(key, (cellMap.get(key) ?? 0) + sub);
    }
  }
  const topCats = Array.from(catTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([c]) => c);
  if (minH > maxH) return { cells: [], categories: topCats, hours: [] };
  const hours: number[] = [];
  for (let h = minH; h <= maxH; h++) hours.push(h);
  const cells: CategoryHourCell[] = [];
  for (const c of topCats) {
    for (const h of hours) {
      cells.push({ category: c, hour: h, sales: cellMap.get(`${c}|${h}`) ?? 0 });
    }
  }
  return { cells, categories: topCats, hours };
};

// カテゴリ × 時間帯ヒートマップをフード／ドリンクで分割
//   ビール vs 豚玉のようにレンジが違うものを同じグリッドで比較しないよう、それぞれ独立したヒートマップを返す
export const categoryHourMatrixByGroup = (
  txns: TxnLite[],
  drinkKeywordsByStore?: Record<string, string[]>,
  topN = 8,
  stores: StoreLD[] = [],
): { food: CategoryHourGrid; drink: CategoryHourGrid } => {
  const foodTotals = new Map<string, number>();
  const drinkTotals = new Map<string, number>();
  const foodCells = new Map<string, number>();
  const drinkCells = new Map<string, number>();
  let minH = Infinity;
  let maxH = 0;
  for (const t of txns) {
    const h = salesAccountHour(t, stores);
    if (h === null) continue;
    if (h < minH) minH = h;
    if (h > maxH) maxH = h;
    for (const it of t.items ?? []) {
      const cat = (it.category ?? '未分類').toString();
      const sub = (it.unitPrice ?? 0) * (it.quantity ?? 0);
      const isDrink = isDrinkCategory(it.category, drinkKeywordsByStore, t.storeId, it.menuName);
      const totals = isDrink ? drinkTotals : foodTotals;
      const cells = isDrink ? drinkCells : foodCells;
      totals.set(cat, (totals.get(cat) ?? 0) + sub);
      const key = `${cat}|${h}`;
      cells.set(key, (cells.get(key) ?? 0) + sub);
    }
  }
  const build = (
    totals: Map<string, number>,
    cellsMap: Map<string, number>,
  ): CategoryHourGrid => {
    const topCats = Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, topN)
      .map(([c]) => c);
    if (minH > maxH || topCats.length === 0) {
      return { cells: [], categories: topCats, hours: [] };
    }
    const hours: number[] = [];
    for (let h = minH; h <= maxH; h++) hours.push(h);
    const cells: CategoryHourCell[] = [];
    for (const c of topCats) {
      for (const h of hours) {
        cells.push({ category: c, hour: h, sales: cellsMap.get(`${c}|${h}`) ?? 0 });
      }
    }
    return { cells, categories: topCats, hours };
  };
  return {
    food: build(foodTotals, foodCells),
    drink: build(drinkTotals, drinkCells),
  };
};

// スタッフ別売上
export interface StaffStat {
  staffName: string;
  txnCount: number;
  customerCount: number;
  sales: number;
  avgSpend: number | null;
}
export const staffStats = (txns: TxnLite[]): StaffStat[] => {
  const map = new Map<string, { sales: number; cust: number; cnt: number }>();
  for (const t of txns) {
    const name = (t.staffName ?? '').trim() || '（未記録）';
    const cur = map.get(name) ?? { sales: 0, cust: 0, cnt: 0 };
    cur.sales += t.total;
    cur.cust += t.customerCount ?? 0;
    cur.cnt += 1;
    map.set(name, cur);
  }
  return Array.from(map.entries())
    .map(([staffName, v]) => ({
      staffName,
      txnCount: v.cnt,
      customerCount: v.cust,
      sales: v.sales,
      avgSpend: v.cust > 0 ? v.sales / v.cust : null,
    }))
    .sort((a, b) => b.sales - a.sales);
};

// 席別回転率
//   営業日数 = txns 内のユニーク日付数
//   1日あたり回転 = txnCount / 営業日数
export interface SeatStat {
  receiptName: string;
  txnCount: number;
  sales: number;
  customerCount: number;
  turnoverPerDay: number | null;
}
export const seatStats = (txns: TxnLite[], operatingDays: number): SeatStat[] => {
  const map = new Map<string, { sales: number; cust: number; cnt: number }>();
  for (const t of txns) {
    const name = (t.receiptName ?? '').trim() || '（席名なし）';
    const cur = map.get(name) ?? { sales: 0, cust: 0, cnt: 0 };
    cur.sales += t.total;
    cur.cust += t.customerCount ?? 0;
    cur.cnt += 1;
    map.set(name, cur);
  }
  return Array.from(map.entries())
    .map(([receiptName, v]) => ({
      receiptName,
      txnCount: v.cnt,
      sales: v.sales,
      customerCount: v.cust,
      turnoverPerDay: operatingDays > 0 ? v.cnt / operatingDays : null,
    }))
    .sort((a, b) => b.sales - a.sales);
};

// 営業日数（取引のあったユニーク日付数）
export const countOperatingDays = (txns: TxnLite[], stores: StoreLD[] = []): number =>
  new Set(txns.map((t) => salesBusinessDate(t, stores))).size;

// 商品 ABC（topProducts ロジックの再実装）
export interface ProductRow {
  menuName: string;
  quantity: number;
  sales: number;
}
export const productAbc = (txns: TxnLite[]): ProductRow[] => {
  const map = new Map<string, { qty: number; sales: number }>();
  for (const t of txns) {
    for (const it of t.items ?? []) {
      const name = it.menuName ?? '';
      if (!name) continue;
      const sub = (it.unitPrice ?? 0) * (it.quantity ?? 0);
      const cur = map.get(name) ?? { qty: 0, sales: 0 };
      cur.qty += it.quantity ?? 0;
      cur.sales += sub;
      map.set(name, cur);
    }
  }
  return Array.from(map.entries())
    .map(([menuName, v]) => ({ menuName, quantity: v.qty, sales: v.sales }))
    .sort((a, b) => b.sales - a.sales);
};

// 商品 ABC をフード / ドリンクで分割
//   isDrinkCategory（カテゴリのキーワード判定）でフード or ドリンクに振り分けて、それぞれ売上降順で返す
export const productAbcByGroup = (
  txns: TxnLite[],
  drinkKeywordsByStore?: Record<string, string[]>,
): { food: ProductRow[]; drink: ProductRow[] } => {
  const foodMap = new Map<string, { qty: number; sales: number }>();
  const drinkMap = new Map<string, { qty: number; sales: number }>();
  for (const t of txns) {
    for (const it of t.items ?? []) {
      const name = it.menuName ?? '';
      if (!name) continue;
      const sub = (it.unitPrice ?? 0) * (it.quantity ?? 0);
      const target = isDrinkCategory(it.category, drinkKeywordsByStore, t.storeId, it.menuName)
        ? drinkMap
        : foodMap;
      const cur = target.get(name) ?? { qty: 0, sales: 0 };
      cur.qty += it.quantity ?? 0;
      cur.sales += sub;
      target.set(name, cur);
    }
  }
  const toRows = (m: Map<string, { qty: number; sales: number }>): ProductRow[] =>
    Array.from(m.entries())
      .map(([menuName, v]) => ({ menuName, quantity: v.qty, sales: v.sales }))
      .sort((a, b) => b.sales - a.sales);
  return { food: toRows(foodMap), drink: toRows(drinkMap) };
};

// ランチ/ディナーごとの主要 KPI
export interface LDKpi {
  segment: 'ランチ' | 'ディナー' | 'その他';
  txnCount: number;
  customerCount: number;
  sales: number;
  avgSpend: number | null;
  avgPartySize: number | null;
}
export const ldKpis = (txns: TxnLite[], stores: StoreLD[]): LDKpi[] => {
  const segs: LDKpi['segment'][] = ['ランチ', 'ディナー', 'その他'];
  const segOf = (s: LDSeg): LDKpi['segment'] =>
    s === 'lunch' ? 'ランチ' : s === 'dinner' ? 'ディナー' : 'その他';
  const map = new Map<LDKpi['segment'], { sales: number; cust: number; cnt: number }>();
  for (const t of txns) {
    const seg = segOf(classifyLD(t, stores));
    const cur = map.get(seg) ?? { sales: 0, cust: 0, cnt: 0 };
    cur.sales += t.total;
    cur.cust += t.customerCount ?? 0;
    cur.cnt += 1;
    map.set(seg, cur);
  }
  return segs.map((s) => {
    const v = map.get(s);
    return {
      segment: s,
      txnCount: v?.cnt ?? 0,
      customerCount: v?.cust ?? 0,
      sales: v?.sales ?? 0,
      avgSpend: v && v.cust > 0 ? v.sales / v.cust : null,
      avgPartySize: v && v.cnt > 0 ? v.cust / v.cnt : null,
    };
  });
};
